<template>
  <div class="pdf-preview">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-wrapper">
      <p>文档加载中...</p>
    </div>

    <!-- PDF内容区域 - 所有页面连续渲染 -->
    <div ref="pdfContainer" class="pdf-container">
      <canvas v-for="pageNum in totalPages" :key="pageNum" :ref="'canvas-' + pageNum" :data-page="pageNum" class="pdf-canvas"></canvas>
    </div>

    <!-- 错误提示 -->
    <div v-if="error && !loading" class="error-wrapper">
      <p>文档加载失败</p>
    </div>
  </div>
</template>

<script>
const PDFJS = require('pdfjs-dist/build/pdf');

// 设置 worker 路径（本地文件）
PDFJS.GlobalWorkerOptions.workerSrc = require('file-loader!pdfjs-dist/build/pdf.worker.min.js');

// 启用注释渲染（关键：显示电子签章）
PDFJS.AnnotationLayer.renderInteractiveForms = true;

export default {
  name: 'PdfPreview',
  props: {
    url: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      error: false,
      pdfDoc: null,
      totalPages: 0,
      // 懒加载相关
      renderedPages: new Set(), // 已渲染的页码集合
      observer: null, // IntersectionObserver 实例
      pendingRenders: new Set(), // 正在渲染中的页码
      maxConcurrentRenders: 2, // 最大并发渲染数（3x超采样内存占用高，限制并发）
    };
  },
  mounted() {
    this.loadPdf();
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    async loadPdf() {
      try {
        this.loading = true;
        const loadingTask = PDFJS.getDocument({
          url: this.url,
          cMapUrl: './cmaps/',
          cMapPacked: true,
          fontExtraProperties: true,
        });
        this.pdfDoc = await loadingTask.promise;
        this.totalPages = this.pdfDoc.numPages;
        await this.$nextTick();
        this.loading = false;
        // 启动懒加载观察器
        this.initIntersectionObserver();
      } catch (err) {
        console.error('PDF加载失败:', err);
        this.error = true;
        this.loading = false;
      }
    },

    // ====== IntersectionObserver 懒加载 ======
    initIntersectionObserver() {
      const PRELOAD_MARGIN = window.innerHeight;

      this.observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const pageNum = parseInt(entry.target.dataset.page);
              this.renderPageLazy(pageNum);
            }
          });
        },
        {
          root: null,
          rootMargin: `${PRELOAD_MARGIN}px 0px ${PRELOAD_MARGIN}px 0px`,
          threshold: 0,
        }
      );

      this.$nextTick(() => {
        for (let i = 1; i <= this.totalPages; i++) {
          const canvasRef = this.$refs['canvas-' + i];
          const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef;
          if (canvas && this.observer) {
            this.observer.observe(canvas);
          }
        }
      });
    },

    async renderPageLazy(pageNum) {
      if (!this.pdfDoc || this.pendingRenders.has(pageNum)) return;

      // 并发限制：超过最大并发数则排队等待
      if (this.pendingRenders.size >= this.maxConcurrentRenders) return;

      // 已渲染过 → 检测 canvas 是否被浏览器回收变空白
      if (this.renderedPages.has(pageNum)) {
        const isBlank = this.checkCanvasBlank(pageNum);
        if (!isBlank) return; // 内容正常，跳过
        // canvas 被回收了，移除标记允许重渲染
        this.renderedPages.delete(pageNum);
      }

      this.pendingRenders.add(pageNum);

      try {
        await this.renderPage(pageNum);
        this.renderedPages.add(pageNum);
      } catch (e) {
        console.warn(`第 ${pageNum} 页渲染失败:`, e.message);
        // 失败也加入已渲染集合，避免无限重试卡死
        this.renderedPages.add(pageNum);
      } finally {
        this.pendingRenders.delete(pageNum);
        // 完成一页后触发下一页渲染（排队机制）
        this.triggerPendingRenders();
      }
    },

    // 触发可视区域内待渲染的页面
    triggerPendingRenders() {
      if (!this.observer || this.pendingRenders.size >= this.maxConcurrentRenders) return;
      for (let i = 1; i <= this.totalPages; i++) {
        if (this.pendingRenders.size >= this.maxConcurrentRenders) break;
        if (!this.renderedPages.has(i) && !this.pendingRenders.has(i)) {
          const canvasRef = this.$refs['canvas-' + i];
          const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef;
          if (canvas) {
            const rect = canvas.getBoundingClientRect();
            const viewHeight = window.innerHeight;
            // 在可视区域或预加载区域内
            if (rect.top < viewHeight + window.innerHeight && rect.bottom > -window.innerHeight) {
              this.renderPageLazy(i);
            }
          }
        }
      }
    },

    // 检测 canvas 是否为空白（iOS Safari 回收离屏 canvas 后会变白）
    checkCanvasBlank(pageNum) {
      try {
        const canvasRef = this.$refs['canvas-' + pageNum];
        const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef;
        if (!canvas || canvas.width === 0 || canvas.height === 0) return true;

        const ctx = canvas.getContext('2d');
        const sampleW = Math.min(50, canvas.width);
        const sampleH = Math.min(50, canvas.height);
        const imageData = ctx.getImageData(0, 0, sampleW, sampleH);
        const data = imageData.data;

        // 抽样检测：只要有非白非透明像素就认为有内容
        for (let i = 0; i < data.length; i += 16) {
          const r = data[i],
            g = data[i + 1],
            b = data[i + 2],
            a = data[i + 3];
          if (a > 10 && (r < 250 || g < 250 || b < 250)) {
            return false; // 有实际内容
          }
        }
        return true; // 全白/全透明 → 被回收
      } catch (e) {
        return false; // 出错时不判定为空白，避免误重渲染
      }
    },

    // ====== 页面渲染（含超采样）======
    async renderPage(pageNum) {
      if (!this.pdfDoc) return;

      const page = await this.pdfDoc.getPage(pageNum);

      const canvasRef = this.$refs['canvas-' + pageNum];
      const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const dpr = window.devicePixelRatio || 1;
      const qualityMultiplier = 2; // 超采样倍数

      // 基础自适应宽度
      const containerWidth = window.innerWidth - 20;
      const viewport = page.getViewport({ scale: 1 });
      // 超采样
      const baseScale = containerWidth / viewport.width;
      const effectiveDpr = dpr * qualityMultiplier;
      const finalScale = baseScale * effectiveDpr;
      const scaledViewport = page.getViewport({ scale: finalScale });

      // 设置canvas实际像素尺寸
      canvas.height = scaledViewport.height;
      canvas.width = scaledViewport.width;
      // CSS 显示尺寸保持原始大小
      canvas.style.width = scaledViewport.width / effectiveDpr + 'px';
      canvas.style.height = scaledViewport.height / effectiveDpr + 'px';
      // 缩放绘图上下文匹配有效dpr
      ctx.scale(effectiveDpr, effectiveDpr);
      // viewport 使用不含dpr的scale
      const outputViewport = page.getViewport({ scale: baseScale });

      // 渲染页面内容
      const renderContext = {
        canvasContext: ctx,
        viewport: outputViewport,
        annotationStorage: new Map(),
      };

      await page.render(renderContext).promise;

      // 渲染注释层（电子签章在此层）
      await this.renderAnnotations(page);
    },

    async renderAnnotations(page) {
      try {
        const annotationsData = await page.getAnnotations();
        const stampAnnotations = annotationsData.filter(
          ann => ann.subtype === 'Stamp' || ann.subtype === 'WIDGET' || ann.annotationType === 15
        );
        if (!stampAnnotations.length && annotationsData.length) return;
      } catch (e) {
        console.warn('注释渲染跳过:', e.message);
      }
    },
  },
};
</script>

<style scoped>
.pdf-preview {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.loading-wrapper,
.error-wrapper {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #666;
  z-index: 100;
  background: rgba(255, 255, 255, 0.95);
  padding: 0.53rem 0.32rem;
  border-radius: 0.11rem;
  box-shadow: 0 0.05rem 0.27rem rgba(0, 0, 0, 0.15);
}

.error-wrapper p {
  margin-bottom: 0.2rem;
  font-size: 0.21rem;
}

.pdf-container {
  padding: 0.133rem 0.133rem 0.4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pdf-canvas {
  margin-bottom: 0.2rem;
  box-shadow: 0 0.03rem 0.13rem rgba(0, 0, 0, 0.1);
  border-radius: 0.05rem;
  background: #fff;
  display: block;
}
</style>